import { useState, useMemo } from 'react'
import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { 
  FileText, ArrowLeft, Clock, Calendar, 
  Tag, ArrowRight, Sparkles, Search, BookOpen
} from 'lucide-react'
import { getAllPosts, getAllCategories, getFeaturedPosts } from '../services/blogService'

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
}

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: 'easeOut' },
  },
}

// Featured Article Card
function FeaturedArticle({ article }) {
  return (
    <Link
      to={`/blog/${article.slug}`}
      className="group block relative overflow-hidden rounded-3xl border border-indigo-500/30 shadow-2xl"
      style={{ backgroundColor: '#0f172a' }}
    >
      {/* Solid dark background as fallback */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-indigo-950 to-slate-900" />
      
      {/* Grid pattern */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff05_1px,transparent_1px),linear-gradient(to_bottom,#ffffff05_1px,transparent_1px)] bg-[size:24px_24px]" />
      
      {/* Glow effects */}
      <div className="absolute top-10 right-10 w-72 h-72 rounded-full bg-indigo-600/30 blur-3xl" />
      <div className="absolute bottom-10 left-10 w-48 h-48 rounded-full bg-purple-600/30 blur-3xl" />
      
      <div className="relative p-8 md:p-12 lg:p-16">
        <div className="flex items-center gap-2 mb-6">
          <Sparkles className="w-5 h-5 text-yellow-400" />
          <span className="text-sm font-semibold text-yellow-400 tracking-wide uppercase">Article à la une</span>
        </div>
        
        <span className="inline-block px-4 py-1.5 text-xs font-semibold text-white bg-indigo-600 rounded-full mb-6">
          {article.category}
        </span>
        
        <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold leading-tight" style={{ color: '#ffffff' }}>
          {article.title}
        </h2>
        
        <p className="mt-6 text-lg leading-relaxed line-clamp-2 max-w-2xl" style={{ color: '#d1d5db' }}>
          {article.description}
        </p>
        
        <div className="flex flex-wrap items-center gap-6 mt-8 text-sm" style={{ color: '#9ca3af' }}>
          <span className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-indigo-400" />
            {new Date(article.date).toLocaleDateString('fr-FR', { 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}
          </span>
          <span className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-indigo-400" />
            {article.readTime}
          </span>
        </div>
        
        <div className="inline-flex items-center gap-2 mt-10 px-6 py-3 bg-indigo-600 hover:bg-indigo-500 text-white font-semibold rounded-full group-hover:gap-3 transition-all">
          Lire l'article
          <ArrowRight className="w-5 h-5" />
        </div>
      </div>
    </Link>
  )
}

// Regular Article Card
function ArticleCard({ article }) {
  return (
    <motion.article
      variants={itemVariants}
      whileHover={{ y: -4 }}
      className="group"
    >
      <Link
        to={`/blog/${article.slug}`}
        className="block relative overflow-hidden bg-white border border-gray-100 shadow-sm transition-all rounded-2xl hover:shadow-xl hover:border-indigo-100"
      >
        {/* Article Image Placeholder */}
        <div className="relative aspect-[16/9] bg-gradient-to-br from-gray-100 to-gray-200 overflow-hidden">
          {article.image ? (
            <img
              src={article.image}
              alt={article.title}
              className="object-cover w-full h-full transition-transform duration-500 group-hover:scale-105"
              loading="lazy"
            />
          ) : (
            <div className="flex items-center justify-center w-full h-full bg-gradient-to-br from-indigo-50 to-purple-50">
              <BookOpen className="w-12 h-12 text-indigo-300" />
            </div>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
        </div>

        {/* Article Content */}
        <div className="p-6">
          <div className="flex items-center gap-3 text-xs text-gray-500">
            <span className="px-2.5 py-1 font-medium text-indigo-600 bg-indigo-50 rounded-full">
              {article.category}
            </span>
            <span className="flex items-center gap-1">
              <Clock className="w-3.5 h-3.5" />
              {article.readTime}
            </span>
          </div>
          
          <h3 className="mt-4 text-xl font-bold text-gray-900 group-hover:text-indigo-600 transition-colors line-clamp-2">
            {article.title}
          </h3>
          
          <p className="mt-2 text-gray-600 line-clamp-2">
            {article.description}
          </p>

          {/* Tags */}
          <div className="flex flex-wrap gap-2 mt-4">
            {article.tags.slice(0, 3).map((tag) => (
              <span
                key={tag}
                className="inline-flex items-center gap-1 px-2 py-0.5 text-xs text-gray-500 bg-gray-100 rounded-full"
              >
                <Tag className="w-3 h-3" />
                {tag}
              </span>
            ))}
          </div>

          <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-100">
            <span className="flex items-center gap-1.5 text-xs text-gray-500">
              <Calendar className="w-3.5 h-3.5" />
              {new Date(article.date).toLocaleDateString('fr-FR', { 
                year: 'numeric', 
                month: 'short', 
                day: 'numeric' 
              })}
            </span>
            <span className="flex items-center gap-1 text-sm font-semibold text-indigo-600 group-hover:gap-2 transition-all">
              Lire
              <ArrowRight className="w-4 h-4" />
            </span>
          </div>
        </div>
      </Link>
    </motion.article>
  )
}

export default function ArticlesPage() {
  const [selectedCategory, setSelectedCategory] = useState('Tous')
  const [searchQuery, setSearchQuery] = useState('')
  
  const allPosts = getAllPosts()
  const categories = getAllCategories()
  const featuredPosts = getFeaturedPosts()

  // Filter articles based on category and search
  const filteredArticles = useMemo(() => {
    let articles = allPosts

    if (selectedCategory !== 'Tous') {
      articles = articles.filter(a => a.category === selectedCategory)
    }

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase()
      articles = articles.filter(a => 
        a.title.toLowerCase().includes(query) ||
        a.description.toLowerCase().includes(query) ||
        a.tags.some(tag => tag.toLowerCase().includes(query))
      )
    }

    return articles
  }, [allPosts, selectedCategory, searchQuery])

  // Get the first featured post for the hero
  const heroArticle = featuredPosts[0]

  return (
    <main className="min-h-screen pt-20 bg-gray-50">
      {/* Hero Section */}
      <section className="relative py-16 overflow-hidden bg-gradient-to-br from-indigo-900 via-indigo-800 to-primary-900 lg:py-24">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff08_1px,transparent_1px),linear-gradient(to_bottom,#ffffff08_1px,transparent_1px)] bg-[size:32px_32px]" />
        <div className="absolute top-20 right-10 w-72 h-72 rounded-full bg-indigo-500/20 blur-3xl" />
        <div className="absolute bottom-10 left-10 w-96 h-96 rounded-full bg-primary-500/20 blur-3xl" />
        
        <div className="relative px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
          <Link
            to="/"
            className="inline-flex items-center gap-2 mb-8 text-sm font-medium text-white/70 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Retour à l'accueil
          </Link>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="flex flex-col items-start gap-8 lg:flex-row lg:items-center"
          >
            <div className="flex items-center justify-center w-24 h-24 rounded-2xl bg-gradient-to-br from-indigo-500 to-indigo-700 shadow-2xl shadow-indigo-900/50">
              <FileText className="w-12 h-12 text-white" />
            </div>
            
            <div className="flex-1">
              <h1 className="text-4xl font-bold text-white sm:text-5xl">
                Blog
              </h1>
              <p className="mt-2 text-xl text-indigo-200">Tech, Cloud, DevOps & Leadership</p>
              <p className="max-w-2xl mt-4 text-white/70">
                Mes réflexions, retours d'expérience et guides pratiques sur la tech, 
                le management et l'entrepreneuriat.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Featured Article */}
      {heroArticle && (
        <section className="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8 -mt-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <FeaturedArticle article={heroArticle} />
          </motion.div>
        </section>
      )}

      {/* Articles Section */}
      <section className="py-12 lg:py-16">
        <div className="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
          {/* Search and Filter */}
          <div className="flex flex-col gap-4 mb-8 md:flex-row md:items-center md:justify-between">
            {/* Search */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="relative flex-1 max-w-md"
            >
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Rechercher un article..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-3 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
              />
            </motion.div>

            {/* Categories Filter */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="flex flex-wrap gap-2"
            >
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-4 py-2 text-sm font-medium transition-all rounded-full ${
                    selectedCategory === category
                      ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-200'
                      : 'bg-white text-gray-600 border border-gray-200 hover:border-indigo-300 hover:text-indigo-600'
                  }`}
                >
                  {category}
                </button>
              ))}
            </motion.div>
          </div>

          {/* Results count */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="mb-6 text-sm text-gray-500"
          >
            {filteredArticles.length} article{filteredArticles.length > 1 ? 's' : ''} trouvé{filteredArticles.length > 1 ? 's' : ''}
          </motion.p>

          {/* Articles Grid */}
          {filteredArticles.length > 0 ? (
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              className="grid gap-8 md:grid-cols-2 lg:grid-cols-3"
            >
              {filteredArticles.map((article) => (
                <ArticleCard key={article.slug} article={article} />
              ))}
            </motion.div>
          ) : (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex flex-col items-center justify-center py-16 text-center"
            >
              <div className="w-16 h-16 mb-4 rounded-full bg-gray-100 flex items-center justify-center">
                <Search className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Aucun article trouvé</h3>
              <p className="mt-2 text-gray-500">
                Essayez une autre recherche ou sélectionnez une catégorie différente.
              </p>
              <button
                onClick={() => {
                  setSearchQuery('')
                  setSelectedCategory('Tous')
                }}
                className="mt-4 text-indigo-600 font-medium hover:text-indigo-800 transition-colors"
              >
                Réinitialiser les filtres
              </button>
            </motion.div>
          )}

          {/* Newsletter CTA - Scaltify */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="mt-16 p-8 md:p-12 bg-gradient-to-br from-indigo-600 to-purple-700 rounded-3xl text-center"
          >
            <h3 className="text-2xl md:text-3xl font-bold text-white">
              Restez informé
            </h3>
            <p className="mt-2 text-indigo-200 max-w-xl mx-auto">
              Recevez des insights hebdomadaires sur la tech, le business et l'entrepreneuriat via la newsletter Scaltify.
            </p>
            <a
              href="https://scaltify.com/newsletter"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block mt-6 px-6 py-3 bg-white text-indigo-600 font-semibold rounded-xl hover:bg-indigo-50 transition-colors"
            >
              S'inscrire à la newsletter Scaltify
            </a>
          </motion.div>
        </div>
      </section>
    </main>
  )
}
